<template>
  <div id="panelBeneficiaries" class="panel panel-default">
    <div class="panel-title top-margin">Beneficiary Information</div>
    <div class="line-separator"></div>

    <question v-if="display"></question>

    <div class="row" v-if="beneficiaries.length === 0">
      <div class="col-lg-12">
        <div class="alert alert-info alert-dismissible" role="alert">
          No beneficiaries found.
        </div>
      </div>
    </div>

    <div
      v-if="beneficiaries.length > 0"
      v-bind:class="{
        'table-fixed-header--md': beneficiaries.length > 10,
        'table-responsive': true,
        'bottom-margin': true,
      }"
    >
      <table class="table table-striped">
        <thead>
          <tr>
            <th class="text-left">Name</th>
            <th class="text-left">Percentage</th>
            <th class="text-center">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(beneficiary, index) in beneficiaries" :key="index">
            <td class="text-left">
              <template v-if="editingIds.includes(beneficiary.id)">
                <input
                  type="text"
                  class="form-control"
                  v-model="beneficiary.beneficiaryName"
                  :class="{
                    'is-invalid': hasNameError(beneficiary.id),
                  }"
                />
                <div
                  v-if="hasNameError(beneficiary.id)"
                  class="invalid-feedback"
                >
                  Beneficiary name is required
                </div>
              </template>
              <template v-else>
                {{ beneficiary.beneficiaryName }}
              </template>
            </td>
            <td class="text-left">
              <template v-if="editingIds.includes(beneficiary.id)">
                <input
                  type="number"
                  class="form-control"
                  v-model.number="beneficiary.percentage"
                  :class="{ 'is-invalid': hasError(beneficiary.id) }"
                />
                <div v-if="hasError(beneficiary.id)" class="invalid-feedback">
                  Total percentage cannot exceed 100% or be less than 0
                </div>
              </template>
              <template v-else> {{ beneficiary.percentage }}% </template>
            </td>
            <td class="text-center">
              <template v-if="editingIds.includes(beneficiary.id)">
                <button
                  @click="saveBeneficiary(beneficiary.id)"
                  type="button"
                  class="btn btn-primary"
                  :disabled="isLoading || hasError(beneficiary.id)"
                >
                  <em class="fa fa-save fa-2x"></em>
                </button>
              </template>
              <template v-else>
                <button
                  @click="editBeneficiary(beneficiary.id)"
                  type="button"
                  class="btn btn-edit"
                  aria-label="Edit beneficiary"
                >
                  <em class="fa fa-edit fa-2x"></em>
                </button>
              </template>
              <button
                @click="deleteBeneficiary(beneficiary.id)"
                type="button"
                class="btn btn-trash"
                :disabled="isLoading"
                aria-label="Delete beneficiary"
              >
                <em class="fa fa-trash fa-2x"></em>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <button
      class="btn btn-default btn-lg btn-add-beneficiary"
      @click="addNewBeneficiary"
      :disabled="beneficiaries.length >= 10"
    >
      Add new beneficiary
    </button>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import Question from '@/components/modals/Question';
import { Events } from '@/data/static/events';
import {
  SHOW_QUESTION_MODAL,
  HIDE_QUESTION_MODAL,
} from '@/store/mutation-types';

export default {
  name: 'AccountBeneficiaries',
  components: {
    Question,
  },
  props: {
    beneficiaries: {
      type: Array,
      required: true,
      default: () => [],
      validator: value =>
        value.every(
          beneficiary =>
            typeof beneficiary.beneficiaryName === 'string' &&
            typeof beneficiary.percentage === 'number' &&
            typeof beneficiary.id === 'number',
        ),
    },
    accountId: {
      type: [Number, String],
      required: false,
      default: 0,
    },
  },
  data() {
    return {
      editingIds: [],
      isLoading: false,
      newBeneficiary: null,
    };
  },
  computed: {
    ...mapState('question', ['display']),
  },
  methods: {
    ...mapActions('account', [
      'deleteAccountBeneficiary',
      'saveAccountBeneficiary',
    ]),
    ...mapMutations('question', {
      showQuestionModal: SHOW_QUESTION_MODAL,
      hideQuestionModal: HIDE_QUESTION_MODAL,
    }),
    addNewBeneficiary() {
      if (this.beneficiaries.length >= 10) return;
      const newBeneficiary = {
        id: `B-${this.beneficiaries.length + 1}`,
        beneficiaryName: '',
        percentage: 0,
      };
      this.beneficiaries.push(newBeneficiary);
      this.editingIds.push(newBeneficiary.id);
    },
    hasError(id) {
      if (this.editingIds.includes(id)) {
        const findBeneficiary = this.beneficiaries.find(
          beneficiary => beneficiary.id === id,
        );
        if (findBeneficiary.percentage > 100 || findBeneficiary.percentage <= 0) {
          return true;
        }
        const totalPercentage = this.beneficiaries.reduce(
          (sum, currentBeneficiary) =>
            sum + parseFloat(currentBeneficiary.percentage),
          0,
        );
        return totalPercentage > 100;
      }
      return false;
    },
    hasNameError(id) {
      if (this.editingIds.includes(id)) {
        const findBeneficiary = this.beneficiaries.find(
          beneficiary => beneficiary.id === id,
        );
        return (
          !findBeneficiary.beneficiaryName ||
          !findBeneficiary.beneficiaryName.trim()
        );
      }
      return false;
    },
    editBeneficiary(id) {
      this.editingIds.push(id);
    },
    async saveBeneficiary(id) {
      if (this.hasError(id) || this.hasNameError(id)) return;
      this.isLoading = true;
      try {
        const updatedBeneficiary = {
          ...this.beneficiaries.find(beneficiary => beneficiary.id === id),
        };
        if (typeof updatedBeneficiary.id === 'string') {
          updatedBeneficiary.id = null;
        }
        const response = await this.saveAccountBeneficiary({
          accountId: this.accountId,
          beneficiary: updatedBeneficiary,
        });
        this.beneficiaries = this.beneficiaries.filter(
          beneficiary => beneficiary.id !== id,
        );
        response.percentage = response.percentage.toFixed(2);
        this.beneficiaries.push(response);

        this.editingIds = this.editingIds.filter(editingId => editingId !== id);
      } catch (error) {
        this.$emit(Events.BENEFICIARY_LOADING_FAILED);
      } finally {
        this.isLoading = false;
      }
    },
    async deleteBeneficiary(id) {
      if (typeof id === 'string') {
        this.editingIds = this.editingIds.filter(
          editingId => editingId !== id,
        );
        this.beneficiaries = this.beneficiaries.filter(
          beneficiary => beneficiary.id !== id,
        );
        return;
      }

      this.showQuestionModal({
        title: 'Delete Beneficiary',
        question: 'Are you sure you want to delete this beneficiary?',
        method: async () => {
          this.isLoading = true;
          this.$emit(Events.BENEFICIARY_LOADING_STARTED);
          try {
            await this.deleteAccountBeneficiary({
              accountId: this.accountId,
              beneficiaryId: id,
            });
            this.editingIds = this.editingIds.filter(editingId => editingId !== id);
            this.beneficiaries = this.beneficiaries.filter(
              beneficiary => beneficiary.id !== id,
            );
            this.$emit(Events.BENEFICIARY_LOADING_SUCCEED);
            this.$emit(Events.BENEFICIARIES_UPDATED);
          } catch (error) {
            this.$emit(Events.BENEFICIARY_LOADING_FAILED);
          } finally {
            this.isLoading = false;
            this.hideQuestionModal();
          }
        },
        yesButtonText: 'Yes',
        hideCancelButton: false,
      });
    },
  },
};
</script>

<style scoped>
.panel-title {
  font-size: 20px;
  margin-bottom: 5px;
}

.btn-trash {
  border: 1px solid #dce0e5;
  background: white;
  margin-left: 5px;
}

.fa-trash {
  color: #f05050;
}

.btn-edit {
  border: 1px solid #dce0e5;
  background: white;
  margin-left: 5px;
}
.fa-edit {
  color: #00788d;
}

.fa-save {
  color: #fff;
}

table {
  border-collapse: collapse;
}

th {
  padding: 14px 8px;
  color: #888;
  border-bottom-width: 1px;
  vertical-align: bottom;
  border-bottom: 1px solid #eee;
  border-top: 0;
  line-height: 1.52857143;
  font-weight: normal;
}

td {
  padding: 8px;
  line-height: 1.52857143;
  vertical-align: top;
  border-top: 1px solid #eee;
  height: 60px;
}

.table-striped > tbody > tr:nth-of-type(odd) {
  background-color: #fafbfc;
}

.panel-default {
  padding: 30px 40px;
}

.line-separator {
  padding: 8px;
  line-height: 1.52857143;
  vertical-align: top;
  border-top: 1px solid #eee;
}

.invalid-feedback {
  display: block;
  color: #f05050;
  font-size: 12px;
  margin-top: 5px;
}

.is-invalid {
  border-color: #f05050;
}

.btn-add-beneficiary {
  margin-top: 15px;
}
</style>



// WEBPACK FOOTER //
// AccountBeneficiaries.vue?d332ea42